import type { ComponentType, ReactNode } from "react";
import { Card, CardContent, CardActionArea, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import { usePermissions } from "react-admin";
import { hasPermission } from "@/utils/permission-utils";
import { ACCOUNTS_DASHBOARD_CARDS } from "@/config/accounts-dashboard-cards";

type DashboardCardProps = {
  icon: ComponentType<SvgIconProps>;
  title: ReactNode;
  description: ReactNode;
  path: string;
};

const DashboardCard = ({ icon: Icon, title, description, path }: DashboardCardProps) => {
  const handleClick = () => {
    window.location.hash = `/${path}`;
    window.dispatchEvent(new Event("hashchange"));
  };

  return (
    <Card sx={{ minHeight: { xs: 120, sm: 150 }, width: "100%" }}>
      <CardActionArea onClick={handleClick} sx={{ height: "100%" }}>
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            px: { xs: 1, sm: 2 },
            py: { xs: 1.5, sm: 2 },
          }}
        >
          <Icon sx={{ fontSize: { xs: 32, sm: 40 }, color: "primary.main", mb: 1 }} />
          <Typography variant="subtitle1" sx={{ fontSize: { xs: "0.95rem", sm: "1.1rem" } }}>
            {title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            {description}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default function AccountsQuickNav() {
  const { permissions } = usePermissions();

  const visibleCards = ACCOUNTS_DASHBOARD_CARDS.filter((c) =>
    hasPermission(permissions, c.permission)
  );

  if (visibleCards.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 2, sm: 3 } }}>
      {visibleCards.map((c) => (
        <Grid
          key={c.path}
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <DashboardCard icon={c.icon} title={c.label} description={c.description} path={c.path} />
        </Grid>
      ))}
    </Grid>
  );
}
